import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, firstValueFrom } from 'rxjs';
import { AlertService } from '@/app/atoms/alert/alert.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import {
  mapApiErrorToFormFields,
  type BankingFormFieldKey,
  toBankingError,
} from '@/app/common/banking/banking-api-errors';
import {
  buildBeneficiaryPayload,
  type BankingFormValues,
} from '@/app/common/banking/banking-payload';
import {
  BANKING_REGIONS,
  type BankingRegion,
  regionForIso,
} from '@/app/common/banking/banking-regions';
import {
  abaRoutingError,
  accountNameError,
  digitsOnly,
  formatIbanDisplay,
  formatSortCodeDisplay,
  ibanError,
  normalizeIban,
  requiredError,
  ukAccountNumberError,
  ukSortCodeError,
  usAccountNumberError,
  usZipError,
} from '@/app/common/banking/banking-validation';
import type { AirwallexBeneficiaryDto } from '@/app/pages/admin-options/settings/molecules/creator-payment-details-fix/creator-payment-details.types';
import { AdminCreatorPaymentDetailsService } from '@/app/pages/admin-options/settings/services/admin-creator-payment-details.service';

const FEATURED_REGION_COUNT = 3;

@Component({
  selector: 'ins-creator-payment-details-form',
  templateUrl: './payment-details-form.html',
  host: { class: 'block h-full min-h-0 w-full min-w-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorPaymentDetailsForm implements OnInit {
  readonly mode = input.required<'add' | 'edit'>();
  readonly beneficiary = input<AirwallexBeneficiaryDto | undefined>();
  readonly hasExisting = input(false);
  readonly creatorId = input.required<string>();

  readonly saved = output<void>();
  readonly cancelled = output<void>();

  readonly featuredRegions = BANKING_REGIONS.slice(0, FEATURED_REGION_COUNT);
  readonly euroAreaRegions = BANKING_REGIONS.slice(FEATURED_REGION_COUNT);

  readonly values = signal<BankingFormValues>(this.emptyValues());
  readonly touched = signal<Partial<Record<BankingFormFieldKey, boolean>>>({});
  readonly serverErrors = signal<Partial<Record<BankingFormFieldKey, string>>>({});
  readonly banner = signal<string | null>(null);
  readonly saving = signal(false);
  readonly savingStep = signal<'validating' | 'saving' | null>(null);

  readonly variant = computed(() => this.values().region?.variant ?? null);

  /** US and Ireland EUR corridors require a beneficiary mailing address. */
  readonly needsAddress = computed(
    () => this.variant() === 'US' || this.values().region?.iso === 'IE',
  );

  readonly clientErrors = computed((): Partial<Record<BankingFormFieldKey, string | null>> => {
    const v = this.values();
    const variant = this.variant();
    const errs: Partial<Record<BankingFormFieldKey, string | null>> = {
      accountName: accountNameError(v.accountName, v.entityType),
    };
    if (variant === 'GB') {
      errs.sortCode = ukSortCodeError(v.sortCode);
      errs.accountNumber = ukAccountNumberError(v.accountNumber);
    } else if (variant === 'US') {
      errs.routingNumber = abaRoutingError(v.routingNumber);
      errs.accountNumber = usAccountNumberError(v.accountNumber);
      errs.street = requiredError(v.street, 'Street address');
      errs.city = requiredError(v.city, 'City');
      errs.state = requiredError(v.state, 'State');
      errs.zip = usZipError(v.zip);
    } else if (variant === 'EUR') {
      errs.iban = ibanError(v.iban, v.region?.iso);
      if (v.region?.iso === 'IE') {
        errs.street = requiredError(v.street, 'Street address');
        errs.city = requiredError(v.city, 'City');
        errs.zip = requiredError(v.zip, 'Eircode');
      }
    }
    return errs;
  });

  readonly hasClientErrors = computed(() => Object.values(this.clientErrors()).some(Boolean));

  readonly saveLabel = computed(() => {
    const step = this.savingStep();
    if (step === 'validating') return 'Checking details…';
    if (step === 'saving') return 'Saving…';
    return this.mode() === 'edit' ? 'Save changes' : 'Save bank details';
  });

  private readonly destroyRef = inject(DestroyRef);
  private readonly paymentDetailsService = inject(AdminCreatorPaymentDetailsService);
  private readonly alertService = inject(AlertService);
  private readonly toastNotificationService = inject(ToastNotificationService);

  ngOnInit(): void {
    this.values.set(this.initialValues(this.beneficiary()));
  }

  fieldError(key: BankingFormFieldKey): string | null {
    if (this.touched()[key] && this.clientErrors()[key]) {
      return this.clientErrors()[key] ?? null;
    }
    return this.serverErrors()[key] ?? null;
  }

  onRegionChange(iso: string): void {
    const region = regionForIso(iso);
    if (!region) return;
    this.values.update((v) => ({
      ...v,
      region,
      sortCode: '',
      routingNumber: '',
      accountNumber: '',
      iban: '',
    }));
    this.touched.set({});
    this.serverErrors.set({});
    this.banner.set(null);
  }

  onEntityTypeChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value as BankingFormValues['entityType'];
    this.setValue('entityType', value);
  }

  onCategoryChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value as BankingFormValues['category'];
    this.setValue('category', value, 'category');
  }

  onTextInput(
    field: Exclude<BankingFormFieldKey, 'region' | 'category'>,
    event: Event,
    options?: { digitsOnly?: boolean; maxLength?: number; normalizeIban?: boolean },
  ): void {
    let value = (event.target as HTMLInputElement).value;
    if (options?.digitsOnly) {
      value = digitsOnly(value).slice(0, options.maxLength ?? value.length);
    } else if (options?.normalizeIban) {
      value = normalizeIban(value).slice(0, options.maxLength ?? value.length);
    }
    this.setValue(field, value, field);
  }

  onSortCodeInput(event: Event): void {
    const value = digitsOnly((event.target as HTMLInputElement).value).slice(0, 6);
    this.setValue('sortCode', value, 'sortCode');
  }

  markTouched(key: BankingFormFieldKey): void {
    this.touched.update((t) => ({ ...t, [key]: true }));
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  async onSave(): Promise<void> {
    if (this.saving()) return;
    this.banner.set(null);
    this.serverErrors.set({});

    const region = this.values().region;
    if (!region) {
      this.banner.set('Choose the country of the bank account first.');
      return;
    }
    if (this.hasClientErrors()) {
      this.touched.set({
        accountName: true,
        sortCode: true,
        routingNumber: true,
        accountNumber: true,
        iban: true,
        street: true,
        city: true,
        state: true,
        zip: true,
      });
      this.banner.set('Please fix the highlighted fields below.');
      return;
    }

    const payload = buildBeneficiaryPayload(region, this.values());
    const creatorId = this.creatorId().trim();
    if (!creatorId) return;

    this.saving.set(true);
    let step: 'validating' | 'saving' = 'validating';
    try {
      this.savingStep.set('validating');
      await firstValueFrom(
        this.paymentDetailsService.validateBeneficiaryAdmin(creatorId, payload).pipe(
          catchError((err: HttpErrorResponse) => {
            throw toBankingError(err);
          }),
          takeUntilDestroyed(this.destroyRef),
        ),
      );

      const confirmed = await this.alertService.confirm({
        title: this.hasExisting() ? 'Update Airwallex beneficiary' : 'Create Airwallex beneficiary',
        message: this.hasExisting()
          ? 'Update bank details for this creator? Their existing Airwallex beneficiary will be updated in place.'
          : 'Create beneficiary for this creator? This sets airwallexId on their payment detail row.',
        confirmText: this.hasExisting() ? 'Save' : 'Create',
        cancelText: 'Cancel',
        type: 'confirm',
        icon: 'warning',
      });
      if (!confirmed) return;

      step = 'saving';
      this.savingStep.set('saving');
      await firstValueFrom(
        this.paymentDetailsService.createBeneficiaryAdmin(creatorId, payload).pipe(
          catchError((err: HttpErrorResponse) => {
            throw toBankingError(err);
          }),
          takeUntilDestroyed(this.destroyRef),
        ),
      );

      void this.toastNotificationService.alert({
        title: 'Bank details saved',
        message: 'Bank details were saved and linked to the creator.',
        type: 'success',
        icon: 'success',
      });
      this.saved.emit();
    } catch (e) {
      const err = toBankingError(e);
      const mapped = mapApiErrorToFormFields(err, region.variant, step);
      this.serverErrors.set(mapped.fieldErrors);
      this.banner.set(mapped.banner);
    } finally {
      this.saving.set(false);
      this.savingStep.set(null);
    }
  }

  formatSortCodeDisplay(value: string): string {
    return formatSortCodeDisplay(value);
  }

  formatIbanDisplay(value: string): string {
    return formatIbanDisplay(value);
  }

  private setValue<K extends keyof BankingFormValues>(
    key: K,
    value: BankingFormValues[K],
    clears?: BankingFormFieldKey,
  ): void {
    this.values.update((v) => ({ ...v, [key]: value }));
    if (clears) {
      this.serverErrors.update((e) => (e[clears] ? { ...e, [clears]: undefined } : e));
    }
  }

  private emptyValues(): BankingFormValues {
    return {
      region: regionForIso('GB'),
      entityType: 'PERSONAL',
      accountName: '',
      sortCode: '',
      routingNumber: '',
      accountNumber: '',
      iban: '',
      category: 'Checking',
      street: '',
      city: '',
      state: '',
      zip: '',
    };
  }

  private initialValues(beneficiary?: AirwallexBeneficiaryDto): BankingFormValues {
    const bd = beneficiary?.beneficiary?.bank_details;
    const address = beneficiary?.beneficiary?.address;
    const iban = normalizeIban(bd?.iban ?? '');
    const region =
      regionForIso(bd?.bank_country_code) ?? (iban ? regionForIso(iban.slice(0, 2)) : regionForIso('GB'));
    const isEur = region?.variant === 'EUR';
    return {
      region: region ?? regionForIso('GB'),
      entityType: beneficiary?.beneficiary?.entity_type === 'COMPANY' ? 'COMPANY' : 'PERSONAL',
      accountName: bd?.account_name ?? '',
      sortCode: region?.variant === 'GB' ? digitsOnly(bd?.account_routing_value1 ?? '') : '',
      routingNumber: region?.variant === 'US' ? digitsOnly(bd?.account_routing_value1 ?? '') : '',
      accountNumber: !isEur ? digitsOnly(bd?.account_number ?? '') : '',
      iban: isEur ? (iban || normalizeIban(bd?.account_number ?? '')) : '',
      category: bd?.bank_account_category === 'Savings' ? 'Savings' : 'Checking',
      street: address?.street_address ?? '',
      city: address?.city ?? '',
      state: address?.state ?? '',
      zip: address?.postcode ?? '',
    };
  }
}
